import { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Calendar, Clock, Info, CheckCircle, ChevronLeft, ChevronRight, XCircle, CalendarDays, Loader2 } from "lucide-react";
import { CalendarGrid } from '../../ui/calendar-grid'
import { useCRUDController, Row, Col, Tabs, Button } from "../../ui/ot";
// import { Card, CardHeader, CardContent, CardTitle } from "../../ui/card";
import { cn } from '../../ui/utils'
import dayjs from "dayjs"
const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const CardHeader = styled.div`
  margin-bottom: 8px;
  display: flex;
  justify-content: ${(props) => (props.$layoutDirection === "horizontal" ? "center" : "flex-start")};
`;

const CardTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;


const CardContent = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.$layoutDirection === "horizontal" ? "row" : "column")};
  gap: 16px;
  justify-content: ${(props) => (props.$layoutDirection === "horizontal" ? "center" : "flex-start")};
  align-items: ${(props) => (props.$layoutDirection === "horizontal" ? "center" : "stretch")};
`;

const fixedHolidays = [
    { start: "01/01", end: "01/01", name: "元旦" },
    { start: "01/28", end: "02/02", name: "春節連假" },
    { start: "02/28", end: "02/28", name: "和平紀念日" },
    { start: "04/03", end: "04/06", name: "清明連假" },
    { start: "05/01", end: "05/01", name: "勞動節" },
    { start: "05/30", end: "06/02", name: "端午連假" },
    { start: "09/06", end: "09/08", name: "中秋連假" },
    { start: "10/10", end: "10/11", name: "國慶連假" }
];

const CalendarWidget = ({ className, showEvents = false, defaultLibrary, holidayLabelDirection = "vertical", layoutDirection = "vertical" }) => {
    const [currentDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
    const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());

    const libraries = [
        { id: "main", name: "介壽校區", location: "行政大樓1樓", description: "主要館藏及綜合閱覽區", color: "blue" },
        { id: "science", name: "巨輪校區", location: "科學大樓2樓", description: "科技類專業館藏", color: "green" },
    ];

    const holidays = fixedHolidays.map((h) => {
        const [startMonth, startDay] = h.start.split("/").map(Number);
        const [endMonth, endDay] = h.end.split("/").map(Number);
        return {
            start: new Date(currentYear, startMonth - 1, startDay),
            end: new Date(currentYear, endMonth - 1, endDay),
            name: h.name,
        };
    });

    const checkHolidayType = (date) => {
        const holiday = holidays.find((h) => date >= h.start && date <= h.end);
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        if (holiday) return { type: "國定假日休館", name: holiday.name };
        if (isWeekend) return { type: "週末休館" };
        return null;
    };

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const prevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const nextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentMonth(today.getMonth());
        setCurrentYear(today.getFullYear());
    };

    const renderTodayStatus = () => {
        const today = new Date();
        const todayStatus = checkHolidayType(today);

        return (
            <div style={{ background: "#f9fafb", padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                <h3 style={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px", display: "flex", alignItems: "center" }}>
                    <Calendar size={16} style={{ marginRight: "6px" }} />
                    今日開館狀態 ({today.getFullYear()}年{today.getMonth() + 1}月{today.getDate()}日)
                </h3>
                <div>
                    {libraries.map((library) => (
                        <div key={library.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                {todayStatus ? (
                                    <XCircle size={16} style={{ color: "red", marginRight: "4px" }} />
                                ) : (
                                    <CheckCircle size={16} style={{ color: "green", marginRight: "4px" }} />
                                )}
                                <span>{library.name}</span>
                            </div>
                            <span style={{ color: todayStatus ? "red" : "green" }}>{todayStatus ? "休館" : "開館"}</span>
                        </div>
                    ))}
                </div>
                {/* {todayStatus && (
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "red", display: "flex", alignItems: "center" }}>
                        <Info size={12} style={{ marginRight: "4px" }} />
                        今日為{todayStatus.type}{todayStatus.name ? ` - ${todayStatus.name}` : ""}
                    </div>
                )} */}
            </div>
        );
    };

    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentYear, currentMonth);
        const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
        const days = [];

        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<div key={`empty-${i}`} style={{ padding: "4px" }}></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentYear, currentMonth, day);
            const isToday = date.toDateString() === new Date().toDateString();
            const holidayStatus = checkHolidayType(date);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            let background = undefined;
            if (isToday) background = "#bfdbfe";
            else if (holidayStatus) background = "#fee2e2";
            else if (isWeekend) background = "#f3f4f6";

            days.push(
                <div
                    key={day}
                    title={holidayStatus?.name || holidayStatus?.type || undefined}
                    style={{
                        padding: "4px",
                        background,
                        borderRadius: "4px",
                        textAlign: "center",
                        cursor: "pointer",
                        position: "relative",
                        fontSize: "12px",
                        display: "flex",
                        flexDirection: holidayLabelDirection === "horizontal" ? "row" : "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {day}
                    {holidayStatus?.type === "國定假日休館" && (
                        <div style={{ fontSize: "10px", color: "red", marginTop: holidayLabelDirection === "horizontal" ? "0px" : "2px", marginLeft: holidayLabelDirection === "horizontal" ? "4px" : "0px" }}>
                            {holidayStatus.name}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                    <button onClick={prevMonth} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <ChevronLeft size={20} />
                    </button>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <select value={currentYear} onChange={(e) => setCurrentYear(Number(e.target.value))}>
                            {Array.from({ length: 21 }, (_, i) => 2015 + i).map((year) => (
                                <option key={year} value={year}>
                                    {year}年
                                </option>
                            ))}
                        </select>
                        <select value={currentMonth} onChange={(e) => setCurrentMonth(Number(e.target.value))}>
                            {Array.from({ length: 12 }, (_, i) => (
                                <option key={i} value={i}>
                                    {i + 1}月
                                </option>
                            ))}
                        </select>
                        <button onClick={goToToday} style={{ background: "none", border: "none", cursor: "pointer" }}>
                            <CalendarDays size={20} />
                        </button>
                    </div>
                    <button onClick={nextMonth} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <ChevronRight size={20} />
                    </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", fontWeight: "500", textAlign: "center", marginBottom: "4px" }}>
                    {["日", "一", "二", "三", "四", "五", "六"].map((d) => (
                        <div key={d}>{d}</div>
                    ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
                    {days}
                </div>
            </div>
        );
    };
    const afatslibraryController = useCRUDController({ apiRoute: "/afats/frontend/library" })

    useEffect(() => {
        afatslibraryController.getData({})
    }, [])
    return (
        <Row>
            <Col span={24}>
                <Card className={className}>
                    <CardHeader $layoutDirection={layoutDirection}>
                        <CardTitle>
                            <Clock size={20} style={{ marginRight: "8px" }} />
                            開館時間
                        </CardTitle>
                    </CardHeader>
                    <CardContent $layoutDirection={layoutDirection}>
                        {/* {renderTodayStatus()} */}
                        {/* {renderCalendar()} */}
                        <Row>
                            <Col span={24}>
                                <LibraryCalendar />
                            </Col>
                        </Row>
                    </CardContent>
                </Card>
            </Col>
        </Row>

    );
}
const LibraryCalendar = ({ }) => {
    const [activeTab, setActiveTab] = useState("001")
    const [showLeftArrow, setShowLeftArrow] = useState(true)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [shouldFillWidth, setShouldFillWidth] = useState(false)
    const tabsRef = useRef(null)
    const tabsContainerRef = useRef(null)
    const measureRef = useRef(null)
    const [currentDate, setCurrentDate] = useState(new Date())
    const [loading, setloading] = useState(false);

    const scrollTabs = (direction) => {
        if (!tabsRef.current) return

        const container = tabsRef.current
        const scrollAmount = container.clientWidth / 2

        if (direction === "left") {
            container.scrollBy({ left: -scrollAmount, behavior: "smooth" })
        } else {
            container.scrollBy({ left: scrollAmount, behavior: "smooth" })
        }
    }

    const handleTabClick = (index, key) => {
        setActiveTab(key)

        // Ensure the clicked tab is visible when in scroll mode
        if (!shouldFillWidth && tabsRef.current) {
            const container = tabsRef.current
            const tabElement = container.children[index]

            if (tabElement) {
                const containerRect = container.getBoundingClientRect()
                const tabRect = tabElement.getBoundingClientRect()

                if (tabRect.left < containerRect.left) {
                    tabElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" })
                } else if (tabRect.right > containerRect.right) {
                    tabElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "end" })
                }
            }
        }
    }

    const checkArrowVisibility = () => {
        if (!tabsRef.current || shouldFillWidth) {
            setShowLeftArrow(false)
            setShowRightArrow(false)
            return
        }

        const container = tabsRef.current
        setShowLeftArrow(container.scrollLeft > 0)
        setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth - 1)
    }

    const measureTabsWidth = () => {
        if (!measureRef.current || !tabsContainerRef.current) return

        // Get the natural width of all tabs
        const measureContainer = measureRef.current
        const availableWidth = tabsContainerRef.current.clientWidth - 16 // Subtract padding

        // Calculate total width needed for all tabs
        let totalTabsWidth = 0
        const tabElements = measureContainer.children

        for (let i = 0; i < tabElements.length; i++) {
            totalTabsWidth += (tabElements[i]).offsetWidth + 4 // Add margin
        }

        // If tabs can fit comfortably, fill the width; otherwise, use scroll mode
        const canFitComfortably = totalTabsWidth <= availableWidth
        setShouldFillWidth(canFitComfortably)
    }


    const getDefaultDateRange = (start_year = new Date().getFullYear() - 5, end_year = new Date().getFullYear() + 5) => {
        const minDate = new Date(start_year, 0, 1);          // 該年的 1 月 1 日
        const maxDate = new Date(end_year, 11, 31);        // 該年的 12 月 31 日
        return { minDate, maxDate };
    }
    const [dateRange, setDateRange] = useState(getDefaultDateRange())
    const afatslibraryController = useCRUDController({
        apiRoute: "/afats/frontend/library",
        onBefore: ({ axiosMode }) => {
            if (axiosMode === "get") {
                setloading(true)
            }
        },
        onFinish: ({ axiosMode }) => {
            if (axiosMode === "get") {
                setloading(false)
            }
        }

    })
    const transformAllHolidayData = (holiday_setting_holiday_data, else_holiday_data) => {
        const officialHolidays = (holiday_setting_holiday_data || [])
            .filter(item => item?.isholiday === "true") // 只取 isholiday == "true"
            .map(item => ({
                name: item?.description,
                color: "bg-yellow-200",
                date: item?.date
            }));

        const customHolidays = (else_holiday_data || [])?.map(item => ({
            name: item?.description,
            color: "bg-blue-200",
            date: item?.date
        }));
        return [...officialHolidays, ...customHolidays]; // 合併兩個陣列
    }
    const checkHolidayType = ({ date, holidays, exceptWeekday }) => {
        const holiday = holidays.find((h) => dayjs(date).format("YYYY-MM-DD") === h?.date);
        let isWeekend = false
        let day = date.getDay()
        if (day === 0) {
            day = 7
        }
        isWeekend = !exceptWeekday.includes(day)
        if (holiday) return { type: "國定假日休館", name: holiday.name };
        if (isWeekend) {
            return { type: "休館日", name: "閉館" }
        }
        return null;
    };
    const renderTodayStatus = () => {
        const today = new Date();
        // const todayStatus = checkHolidayType(today);

        return (
            <div style={{ background: "#f9fafb", padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                <h3 style={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px", display: "flex", alignItems: "center" }}>
                    <Calendar size={16} style={{ marginRight: "6px" }} />
                    今日開館狀態 ({today.getFullYear()}年{today.getMonth() + 1}月{today.getDate()}日)
                </h3>
                <div>
                    {afatslibraryController?.ResponseData?.map((library) => {
                        let library_space_management_data = library?.library_space_management_data || []
                        let library_space_management_first = library_space_management_data?.find((e) => true)

                        let space_management_position_data = library_space_management_first?.space_management_position_data || []
                        let space_management_position_first = space_management_position_data?.find((e) => true)

                        let space_management_position_data_in = space_management_position_first?.space_management_position_data || []
                        let space_management_position_in_first = space_management_position_data_in?.find((e) => true)

                        let space_management_schedule_data = space_management_position_in_first?.space_management_schedule_data || []
                        let space_management_schedule_first = space_management_schedule_data?.find((e) => true)

                        let schedule_class_data = space_management_schedule_first?.schedule_class_data || []
                        let schedule_holiday_data = space_management_schedule_first?.schedule_holiday_data || []
                        let schedule_holiday_data_first = schedule_holiday_data?.find(e => true)
                        let allHolidaySetting = schedule_holiday_data
                            ?.map(item => item?.holiday_setting_holiday_data || [])
                            .flat();
                        let allElseHoliday = schedule_holiday_data
                            ?.map(item => item?.else_holiday_data || [])
                            .flat();

                        let exceptWeekday = schedule_class_data?.filter((ele) => ele?.weekday !== null)
                            .map((value) => {
                                return value?.weekday
                            })

                        // let holidays = transformAllHolidayData(schedule_holiday_data_first?.holiday_setting_holiday_data, schedule_holiday_data_first?.else_holiday_data)
                        let holidays = transformAllHolidayData(allHolidaySetting, allElseHoliday)
                        let libraryTodayStatus = checkHolidayType({ date: today, holidays, exceptWeekday, schedule_class_data })
                        let openString = ""
                        if (!libraryTodayStatus) {
                            let day = today.getDay()
                            if (day === 0) {
                                day = 7
                            }
                            let openArray = schedule_class_data.filter((ele) => {
                                return ele?.weekday === day
                            })
                            openArray.forEach(element => {
                                let startTimeDayjs = dayjs(element?.class_working_start_time, 'HH:mm:ss')
                                let endTimeDayjs = dayjs(element?.class_working_end_time, 'HH:mm:ss')
                                let startTime = startTimeDayjs.format('HH:mm')
                                let endTime = endTimeDayjs.format('HH:mm')
                                openString += `${startTime}~${endTime} `
                            });
                        }
                        return <div key={library?.branchcode} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                {libraryTodayStatus ? (
                                    <XCircle size={16} style={{ color: "red", marginRight: "4px" }} />
                                ) : (
                                    <CheckCircle size={16} style={{ color: "green", marginRight: "4px" }} />
                                )}
                                <span>{library?.branchname} </span>
                            </div>
                            <span style={{ color: "blue" }}>{openString}</span>
                            <span style={{ color: libraryTodayStatus ? "red" : "green" }}>{libraryTodayStatus ? "休館" : "開館"}</span>
                        </div>
                    })}
                </div>
                {/* {todayStatus && (
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "red", display: "flex", alignItems: "center" }}>
                        <Info size={12} style={{ marginRight: "4px" }} />
                        今日為{todayStatus.type}{todayStatus.name ? ` - ${todayStatus.name}` : ""}
                    </div>
                )} */}
            </div>
        );
    };
    useEffect(() => {
        afatslibraryController.getData({})
    }, [])
    useEffect(() => {
        const container = tabsRef.current
        if (!container) return

        // Measure tabs width on mount and resize
        measureTabsWidth()

        container.addEventListener("scroll", checkArrowVisibility)
        window.addEventListener("resize", () => {
            measureTabsWidth()
            checkArrowVisibility()
        })

        // Initial check for arrows
        checkArrowVisibility()

        return () => {
            container.removeEventListener("scroll", checkArrowVisibility)
            window.removeEventListener("resize", measureTabsWidth)
        }
    }, [shouldFillWidth, afatslibraryController?.ResponseData])
    return (<Row>
        {/* <Col span={24}>
            <Tabs
                items={afatslibraryController.ResponseData.map((value) => {
                    return {
                        key: value?.branchcode,
                        tab: value?.branchname,
                    }
                })}
            />
        </Col> */}
        <Col span={24}>
            {renderTodayStatus()}
        </Col>
        <div className="w-full">
            {/* Hidden measurement container */}
            <div ref={measureRef} className="flex opacity-0 pointer-events-none absolute -top-96" aria-hidden="true">
                {afatslibraryController.ResponseData.map((tab, index) => (
                    <div
                        key={`measure-${tab?.branchcode}`}
                        className="flex-shrink-0 px-2 py-1 mx-0.5 rounded text-xs font-medium whitespace-nowrap"
                    >
                        {tab?.branchname}
                    </div>
                ))}
            </div>

            <div className="relative" ref={tabsContainerRef}>
                {/* Left Arrow Button */}
                <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                        "absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full h-6 w-6 shadow-lg transition-opacity duration-300",
                        !showLeftArrow && "opacity-0 pointer-events-none",
                    )}
                    onClick={() => scrollTabs("left")}
                    aria-label="Scroll tabs left"
                >
                    <ChevronLeft className="h-3 w-3" />
                </Button>

                {/* Tabs Navigation */}
                <div
                    className={cn(
                        "flex py-0.5 border rounded-md transition-all duration-300",
                        shouldFillWidth ? "px-2" : "overflow-x-auto scrollbar-hide px-7",
                    )}
                    ref={tabsRef}
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                    {afatslibraryController?.ResponseData.map((tab, index) => (
                        <button
                            key={tab?.branchcode}
                            onClick={() => handleTabClick(index, tab?.branchcode)}
                            className={cn(
                                "px-2 py-1 mx-0.5 rounded text-xs font-medium transition-all duration-200 whitespace-nowrap",
                                shouldFillWidth ? "flex-grow" : "flex-shrink-0",
                                activeTab === tab?.branchcode
                                    ? "bg-primary text-primary-foreground shadow-sm"
                                    : "hover:bg-accent hover:text-accent-foreground",
                            )}
                        >
                            {tab?.branchname}
                        </button>
                    ))}
                </div>

                {/* Right Arrow Button */}
                <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                        "absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full h-6 w-6 shadow-lg transition-opacity duration-300",
                        !showRightArrow && "opacity-0 pointer-events-none",
                    )}
                    onClick={() => scrollTabs("right")}
                    aria-label="Scroll tabs right"
                >
                    <ChevronRight className="h-3 w-3" />
                </Button>
            </div>
        </div>
        {
            loading ?
                <FullScreenLoader />
                : afatslibraryController?.ResponseData
                    .filter((ele) => ele?.branchcode === activeTab)
                    .map((record) => {
                        let library_space_management_data = record?.library_space_management_data || []
                        let library_space_management_first = library_space_management_data?.find((e) => true)

                        let space_management_position_data = library_space_management_first?.space_management_position_data || []
                        let space_management_position_first = space_management_position_data?.find((e) => true)

                        let space_management_position_data_in = space_management_position_first?.space_management_position_data || []
                        let space_management_position_in_first = space_management_position_data_in?.find((e) => true)

                        let space_management_schedule_data = space_management_position_in_first?.space_management_schedule_data || []
                        let space_management_schedule_first = space_management_schedule_data?.find((e) => true)

                        let schedule_class_data = space_management_schedule_first?.schedule_class_data || []
                        let schedule_holiday_data = space_management_schedule_first?.schedule_holiday_data || []
                        let schedule_holiday_data_first = schedule_holiday_data?.find(e => true)
                        let allHolidaySetting = schedule_holiday_data
                            ?.map(item => item?.holiday_setting_holiday_data || [])
                            .flat();
                        let allElseHoliday = schedule_holiday_data
                            ?.map(item => item?.else_holiday_data || [])
                            .flat();

                        let exceptWeekday = schedule_class_data?.filter((ele) => ele?.weekday !== null)
                            .map((value) => {
                                return {
                                    weekday: value?.weekday
                                }
                            })
                        return (<Col span={24}>
                            <CalendarGrid
                                show={false}
                                dateRange={dateRange}
                                // holidays={transformAllHolidayData(schedule_holiday_data_first?.holiday_setting_holiday_data, schedule_holiday_data_first?.else_holiday_data)}
                                holidays={transformAllHolidayData(allHolidaySetting, allElseHoliday)}
                                exceptWeekday={exceptWeekday}
                                size="small"
                                onDateChange={(val) => {
                                    setCurrentDate(val)
                                }}
                                showHolidayTooltip={true}
                            />
                        </Col>)
                    })
        }
    </Row>)
}
function FullScreenLoader({ message = "資料載入中，請稍候…", className }) {
    return (
        <div className={cn("flex items-center justify-center w-full py-4", className)}>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground sm:h-6 sm:w-6" />
        </div>
    );
}
export default CalendarWidget;
